import React, { useMemo, useState, useEffect, useRef } from "react";
import { getCentersList } from "../../../../client/centers/index";
import SelectField from "../../../forms/selectOption";
import { listFileNames } from "../../../../client/seasonDocumentSuppliers";
import ComboboxWithSuggestions from "../../../forms/comboBoxWithSuggestions";
import { MODALS } from "../../../../config/images";
const ModalUploadWithSelect = ({
  open,
  onClose,
  onSubmit,
  text = "Selecciona el documento al que pertenece tu archivo",
  options = [],
  filesCount = 1,
  seasonId,
  supplierId,
}) => {
  const [selected, setSelected] = useState("");
  const [selectedCenter, setSelectedCenter] = useState(""); 
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [centers, setCenters] = useState([]);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [centersLoaded, setCentersLoaded] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileNames, setFileNames] = useState([]);
  const [fileNameErrors, setFileNameErrors] = useState([]);
  const [availableFileNames, setAvailableFileNames] = useState([]); 
  const [loadingFileNames, setLoadingFileNames] = useState(false); 
  const [selectedDocumentStatus, setSelectedDocumentStatus] = useState(null);

  const fileInputRef = useRef(null);

  const ALLOWED_FILE_TYPES = [
    ".pdf", ".doc", ".docx", ".odt", 
    ".xls", ".xlsx", ".ods", ".ppt", 
    ".pptx", ".odp", ".csv", ".jpg", 
    ".jpeg", ".png"
  ];
  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  useEffect(() => {
    if (!centersLoaded) {
      loadCenters();
    }
  }, [centersLoaded]);

  const selectedOpt = useMemo(
    () => options.find(o => o.value === selected),
    [options, selected]
  );

  const allowMultiple = !!selectedOpt?.multiple;
  const requiresCenter = !!selectedOpt?.perPlant; 

    const isDocumentRejected = selectedOpt?.status === 4;


  const invalidForCount = selected && !allowMultiple && filesCount > 1;
  const invalidCenter = selected && requiresCenter && !selectedCenter; 
  const hasFileErrors = fileNameErrors.some(error => error);
  const noFilesSelected = files.length === 0;

  useEffect(() => {
    if (!requiresCenter) setSelectedCenter(""); 
  }, [requiresCenter]);

  const loadCenters = async () => {
    try {
      setLoadingCenters(true);
      const centersData = await getCentersList();
      
      let centersArray = centersData;
      
      if (centersData && centersData.data) {
        centersArray = centersData.data;
      }
      
      if (centersArray && typeof centersArray === 'object' && !Array.isArray(centersArray)) {
        centersArray = Object.values(centersArray);
      }
      
      if (!Array.isArray(centersArray)) {
        setCenters([]);
        return;
      }
      
      const formattedCenters = centersArray.map(center => ({
        value: center.id.toString(),
        label: center.name
      }));
      setCenters(formattedCenters);
      setCentersLoaded(true);
      
    } catch (error) {
      console.error("Error loading centers:", error);
      setCenters([]);
    } finally {
      setLoadingCenters(false);
    }
  }

const loadFileNames = async (sdsId) => {
  if (!sdsId) {
    setAvailableFileNames([]);
    return;
  }
  
  try {
    setLoadingFileNames(true);
    
    const response = await listFileNames(Number(sdsId));
        
    const fileNamesList = response.fileNames || [];
    
    setAvailableFileNames(fileNamesList);
  } catch (error) {
    setAvailableFileNames([]);
  } finally {
    setLoadingFileNames(false);
  }
}

  useEffect(() => {
    if (selected && isDocumentRejected) {
      loadFileNames(selected);
    } else {
      setAvailableFileNames([]);
    }
  }, [selected, isDocumentRejected]);


  const handleDocumentChange = (e) => {
    const newSdsId = e.target.value;
    
    setSelected(newSdsId);
    
    setFiles([]);
    setFileNames([]);
    setFileNameErrors([]);
    setAvailableFileNames([]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  const isValidFileType = (fileName) => {
    const extension = fileName.toLowerCase().slice((fileName.lastIndexOf(".") - 1 >>> 0) + 2);
    return ALLOWED_FILE_TYPES.includes(`.${extension}`);
  };

  const isValidFileSize = (fileSize) => {
    return fileSize <= MAX_FILE_SIZE;
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validationErrors = [];
    const validFiles = [];

    setError("");

    selectedFiles.forEach((file, index) => {
      const fileTypeError = !isValidFileType(file.name);
      const fileSizeError = !isValidFileSize(file.size);
      
      if (fileTypeError || fileSizeError) {
        let errorMsg = "";
        
        if (fileTypeError && fileSizeError) {
          errorMsg = `"${file.name}": Tipo no permitido y supera el tamaño máximo de 50MB`;
        } else if (fileTypeError) {
          errorMsg = `"${file.name}": Tipo de archivo no permitido. Formatos aceptados: ${ALLOWED_FILE_TYPES.join(", ")}`;
        } else if (fileSizeError) {
          errorMsg = `"${file.name}": Supera el tamaño máximo de 50MB (${(file.size / (1024 * 1024)).toFixed(2)}MB)`;
        }
        
        validationErrors.push(errorMsg);
      } else {
        validFiles.push(file);
      }
    });

    if (validationErrors.length > 0) {
      setError(validationErrors.join(" | "));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      
      if (validFiles.length === 0) {
        return;
      }
    }

    if (!allowMultiple && validFiles.length > 1) {
      setError("Este documento solo admite un archivo");
      return;
    }

    if (!allowMultiple && files.length > 0) {
      setFiles(validFiles);
      setFileNames(validFiles.map(() => "")); 
      setError("");
    } else {
      setFiles(prev => [...prev, ...validFiles]);
      setFileNames(prev => [...prev, ...validFiles.map(() => "")]); 
    }
    
    setFileNameErrors(new Array(validFiles.length).fill(true));
  };

  const handleFileNameChange = (index, value) => {
    const newFileNames = [...fileNames];
    newFileNames[index] = value;
    setFileNames(newFileNames);

    const newErrors = [...fileNameErrors];
    if (!value.trim()) {
      newErrors[index] = true;
    } else {
      newErrors[index] = false;
    }
    setFileNameErrors(newErrors);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFileNames(prev => prev.filter((_, i) => i !== index));
    setFileNameErrors(prev => prev.filter((_, i) => i !== index));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };


  const handleSend = async () => {
    const hasEmptyTitles = fileNames.some(name => !name.trim());
    const hasFileErrors = fileNameErrors.some(error => error);
    
    const invalidFiles = files.filter(file => 
      !isValidFileType(file.name) || !isValidFileSize(file.size)
    );
    
    if (invalidFiles.length > 0) {
      setError("Algunos archivos no cumplen con los requisitos de tipo o tamaño");
      return;
    }

    if (!selected || sending || invalidForCount || invalidCenter || noFilesSelected || hasEmptyTitles || hasFileErrors)
      return;
    try {
      setError("");
      setSending(true);

      await onSubmit({
        sdsId: selected,
        centerId: selectedCenter || undefined,
        files: files,
        fileNames: fileNames
      });

      setFiles([]);
      setFileNames([]);
      setFileNameErrors([]);
      setSelected("");
      setSelectedCenter("");
      setAvailableFileNames([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

    } catch (e) {
      setError(e?.message || "No se pudo enviar.");
      setSending(false);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setFileNames([]);
    setFileNameErrors([]);
    setSelected("");
    setSelectedCenter("");
    setError("");
    setAvailableFileNames([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };
  

  if (!open) return null;


  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center w-full bg-black/50">
      <div className="w-[90%] sm:w-[70%] max-h-[90%] overflow-y-auto lg:w-[43%] bg-white rounded-3xl shadow-lg p-4 flex flex-col gap-3">
        <div className="flex justify-end">
          <button onClick={handleClose} className="rounded-md" disabled={sending} aria-disabled={sending}>
            <img src={MODALS.CLOSE} alt="cerrar"/>
          </button>
        </div>
        <div className="flex flex-col items-center justify-center gap-3 pb-3">
          <svg width="51" height="50" viewBox="0 0 51 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M39.0423 41.666C39.0423 41.9423 38.9326 42.2072 38.7372 42.4026C38.5419 42.5979 38.2769 42.7077 38.0006 42.7077H27.584V44.791C27.584 45.148 27.5493 45.4952 27.4798 45.8327H38.0006C39.1057 45.8327 40.1655 45.3937 40.9469 44.6123C41.7283 43.8309 42.1673 42.7711 42.1673 41.666V20.4743C42.1671 19.3694 41.7279 18.3097 40.9465 17.5285L28.8027 5.38685L28.7007 5.30352L28.6257 5.24102C28.4826 5.10538 28.3302 4.98007 28.1694 4.86602C28.1153 4.83294 28.0597 4.80235 28.0027 4.77435L27.9027 4.72435L27.7986 4.66393C27.6861 4.59935 27.5715 4.53268 27.4527 4.48268C27.0376 4.31779 26.5987 4.22072 26.1527 4.19518C26.1111 4.1924 26.0701 4.18754 26.0298 4.1806L25.859 4.16602H13.0007C11.8956 4.16602 10.8358 4.605 10.0544 5.3864C9.27297 6.16781 8.83398 7.22761 8.83398 8.33268V22.916H11.959V8.33268C11.959 8.05641 12.0687 7.79146 12.2641 7.59611C12.4594 7.40076 12.7244 7.29102 13.0007 7.29102H25.5007V16.666C25.5007 17.7711 25.9396 18.8309 26.721 19.6123C27.5024 20.3937 28.5622 20.8327 29.6673 20.8327H39.0423V41.666ZM28.6257 9.62643L36.7048 17.7077H29.6673C29.3911 17.7077 29.1261 17.5979 28.9307 17.4026C28.7354 17.2072 28.6257 16.9423 28.6257 16.666V9.62643ZM10.9173 24.9993H17.1673V29.166H10.9173V24.9993ZM5.70898 24.9993H8.83398V30.2077C8.83398 30.4839 8.94373 30.7489 9.13908 30.9443C9.33443 31.1396 9.59938 31.2493 9.87565 31.2493H18.209C18.4853 31.2493 18.7502 31.1396 18.9456 30.9443C19.1409 30.7489 19.2507 30.4839 19.2507 30.2077V24.9993H20.0402C20.8688 25.0001 21.6631 25.3298 22.2486 25.916L24.5861 28.2493C24.8765 28.5399 25.1067 28.8849 25.2636 29.2645C25.4206 29.6442 25.5011 30.051 25.5007 30.4618V44.791C25.5007 45.6198 25.1714 46.4147 24.5854 47.0007C23.9993 47.5868 23.2045 47.916 22.3757 47.916H21.334V36.4577C21.334 36.1814 21.2242 35.9165 21.0289 35.7211C20.8335 35.5258 20.5686 35.416 20.2923 35.416H7.79232C7.51605 35.416 7.2511 35.5258 7.05575 35.7211C6.8604 35.9165 6.75065 36.1814 6.75065 36.4577V47.916H5.70898C4.88018 47.916 4.08533 47.5868 3.49928 47.0007C2.91322 46.4147 2.58398 45.6198 2.58398 44.791V28.1243C2.58398 27.2955 2.91322 26.5007 3.49928 25.9146C4.08533 25.3286 4.88018 24.9993 5.70898 24.9993ZM19.2507 37.4993V47.916H8.83398V37.4993H19.2507Z"
              fill="#007696" />
          </svg>
          <p className="text-center text-[14px] font-itcdemi">{text}</p>

          <div className="w-full">
            <label className="block mb-2 text-sm text-gray-700 font-itcbook">
              Selecciona el documento <span className="text-red-500">*</span>
            </label>
            <SelectField
              name="document"
              value={selected}
              onChange={handleDocumentChange}
              options={options}
              placeholder="-- Seleccione una opción --"
              disabled={sending}
              width="w-full"
              bgColor="bg-white"
              border="border-gray-300"
              inModal={true}
            />
            {!!selected && (
              <p className="mt-1 text-xs text-gray-500 font-itcbook">
                {allowMultiple ? "Este documento admite múltiples archivos." : "Este documento solo admite un archivo."}
                {requiresCenter ? " — Requiere seleccionar un centro." : ""} 
              </p>
            )}
          </div>
          {requiresCenter && ( 
            <div className="w-full">
              <label className="block mb-2 text-sm text-gray-700 font-itcbook">
                Selecciona el centro <span className="text-red-500">*</span>
              </label>
              <SelectField
                name="center"
                value={selectedCenter}
                onChange={(e) => setSelectedCenter(e.target.value)}
                options={centers}
                placeholder="-- Seleccione un centro --"
                disabled={sending || loadingCenters}
                width="w-full"
                bgColor="bg-white"
                border="border-gray-300"
                inModal={true}
              />
              {loadingCenters && (
                <p className="mt-1 text-xs text-gray-500 font-itcbook">Cargando centros...</p>
              )}
              {invalidCenter && (
                <div className="w-full px-3 py-2 mt-2 text-xs border font-itcbook rounded-xl border-amber-200 bg-amber-50 text-amber-800">
                  Este documento es por centro; selecciona un centro.
                </div>
              )}

            </div>
          )}
          {selected && (
            <div className="w-full">
              <label className="block mb-2 text-sm text-gray-700 font-itcbook">
                {allowMultiple ? "Seleccionar archivos" : "Seleccionar archivo"} <span className="text-red-500">*</span>
              </label>
              <div className="mb-1 text-xs text-gray-500 font-itcbook">
                Formatos permitidos: {ALLOWED_FILE_TYPES.join(", ")} | Tamaño máximo: 50MB por archivo
              </div>
              <button
                type="button"
                onClick={triggerFileInput}
                disabled={sending}
                className="flex items-center justify-center w-full gap-2 p-3 text-sm text-gray-700 transition-colors border border-gray-300 font-itcbook rounded-xl hover:bg-gray-50"
              >
                {files.length === 0 
                  ? `Haz click para ${allowMultiple ? "subir archivos" : "subir un archivo"}`
                  : `${files.length} archivo(s) seleccionado(s)`
                }

                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_1_18127)">
                  <path d="M16.4405 15.1001C20.0405 14.7901 21.5105 12.9401 21.5105 8.8901V8.7601C21.5105 4.2901 19.7205 2.5001 15.2505 2.5001H8.74047C4.27047 2.5001 2.48047 4.2901 2.48047 8.7601V8.8901C2.48047 12.9101 3.93047 14.7601 7.47047 15.0901"
                  stroke="#374151" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12 22V9.12"
                  stroke="#374151" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M15.3504 11.3496L12.0004 7.99961L8.65039 11.3496"
                  stroke="#374151" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_1_18127">
                  <rect width="24" height="24" fill="white" transform="matrix(1 0 0 -1 0 24)"/>
                  </clipPath>
                  </defs>
                </svg>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                multiple={allowMultiple}
                accept={ALLOWED_FILE_TYPES.join(",")}
              />
            </div>
          )}

          {/* Lista de archivos seleccionados */}
          {files.length > 0 && (
            <div className="w-full">
              <h4 className="mb-2 text-sm font-medium text-gray-700 font-itcbook">
                Archivos seleccionados:
              </h4>
              <div className="space-y-3">
                {files.map((file, index) => {
                  const isValidType = isValidFileType(file.name);
                  const isValidSize = isValidFileSize(file.size);
                  const isFileValid = isValidType && isValidSize;
                  
                  return (
                    <div key={index} className={`flex flex-col gap-1 p-3 border rounded-lg ${isFileValid ? 'border-gray-200' : 'border-red-300 bg-red-50'}`}>
                      <div className="flex items-center justify-between w-full">
                        <p className="text-xs text-gray-600 font-itcbook">
                          Título <span className="text-red">*</span>:
                        </p>

                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                          disabled={sending}
                          title="Eliminar archivo"
                        >
                          ×
                        </button>
                      </div>


                      <div className="flex items-center justify-between w-full gap-2">
                        <div className="flex flex-col w-full gap-1">
                          {isDocumentRejected ? (
                            <ComboboxWithSuggestions
                              value={fileNames[index] || ""}
                              onChange={(value) => handleFileNameChange(index, value)}
                              suggestions={availableFileNames}
                              placeholder="Seleccione o ingrese un título"
                              disabled={sending}
                              loading={loadingFileNames}
                              error={fileNameErrors[index]}
                              maxLength={50}
                            />
                          ) : (
                            <input
                              type="text"
                              value={fileNames[index] || ""}
                              maxLength={50}
                              onChange={(e) => handleFileNameChange(index, e.target.value)}
                              className={`w-full outline-none border font-itcbook rounded px-2 py-2 text-sm ${
                                fileNameErrors[index] 
                                  ? 'border-red-300 bg-red-50' 
                                  : 'border-gray-300'
                              }`}
                              placeholder="Ingrese un título"
                              disabled={sending}
                            />
                          )}
                        </div>

                        <div className="flex flex-col items-end">
                          <span className="text-xs text-gray-600 truncate font-itcbook">
                            {file.name} ({(file.size / 1024).toFixed(0)} KB)
                          </span>
                          {!isValidType && (
                            <span className="text-xs text-red-600 font-itcbook">
                              Tipo no permitido
                            </span>
                          )}
                          {!isValidSize && (
                            <span className="text-xs text-red-600 font-itcbook">
                              Supera 50MB ({(file.size / (1024 * 1024)).toFixed(2)}MB)
                            </span>
                          )}
                        </div>
                      </div>
                      
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mensajes de error */}
          {invalidForCount && (
            <div className="w-full px-3 py-2 text-xs border rounded-xl border-amber-200 bg-amber-50 text-amber-800">
              Este documento solo admite <b>un</b> archivo y has seleccionado <b>{files.length}</b>.
              Por favor, selecciona solo un archivo.
            </div>
          )}


          {noFilesSelected && selected && (
            <div className="w-full px-3 py-2 text-xs border font-itcbook rounded-xl border-amber-200 bg-amber-50 text-amber-800">
              Por favor, selecciona al menos un archivo.
            </div>
          )}

          {error && (
            <div className="w-full px-3 py-2 text-xs text-red-700 border border-red-200 font-itcbook rounded-xl bg-red-50">
              {error}
            </div>
          )}

          {/* Botón de envío */}
          <button
            onClick={handleSend}
            disabled={
              !selected ||
              sending ||
              invalidForCount ||
              invalidCenter ||
              noFilesSelected ||
              hasFileErrors ||
              fileNames.some(name => !name.trim()) || 
              fileNameErrors.some(error => error) ||
              files.some(file => !isValidFileType(file.name) || !isValidFileSize(file.size))

            }
            className={`w-full rounded-2xl py-3 mt-2 font-itcdemi transition flex items-center justify-center gap-2
               ${(!selected || sending || invalidForCount || invalidCenter || noFilesSelected || hasFileErrors || fileNames.some(name => !name.trim()) || fileNameErrors.some(error => error) || files.some(file => !isValidFileType(file.name) || !isValidFileSize(file.size))) 
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-[#00A3E0] text-white hover:opacity-90 active:opacity-80"}`}
          >
            {sending && (
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
            )}
            {sending ? "Enviando…" : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalUploadWithSelect;